// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@steiner/App/Components/Common/XngZone";
import ProductActions from "@steiner/App/Components/Product/ProductActions";
import ProductDetailsButtons from "@steiner/App/Components/Product/ProductDetailsButtons";
import SubstituteProductsLink from "@steiner/App/Components/Product/SubstituteProductsLink";
import RequiredProducts from "@steiner/App/Components/ProductDetails/RequiredProducts";
import { CustomAppState } from "@steiner/App/Store/Reducers";
import { xIsAuthenticated } from "@steiner/App/Store/Selectors/SessionSelectors";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => {
    return {
        isAuthenticated: xIsAuthenticated(state),
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & HasProduct;

export interface ProductDetailsViewStyles {
    container?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    leftColumnInnerContainer?: GridContainerProps;
    topGridItem?: GridItemProps;
    middleGridItem?: GridItemProps;
    middleGridItemHidden?: HiddenProps;
    bottomGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    rightColumnGridItemHidden?: HiddenProps;
    // XNG-Changes
    productNumbersWrapper?: InjectableCss;
    specificationsContainer?: InjectableCss;
    divider?: InjectableCss;
    showOnTablet?: HiddenProps;
    showOnMobile?: InjectableCss;
    hideOnMobile?: InjectableCss;
    inventoryWrapper?: InjectableCss;
    hideOnTablet?: HiddenProps;
}

export const viewStyles: ProductDetailsViewStyles = {
    leftColumnGridItem: {
        width: [12, 12, 8, 8, 8],
        css: css`
            overflow: hidden;
            direction: ltr;
        `,
    },
    topGridItem: { width: 12 },
    middleGridItem: { width: [12, 12, 0, 0, 0] },
    middleGridItemHidden: {
        above: "sm",
        css: css`
            width: 100%;
            direction: ltr;
        `,
    },
    bottomGridItem: { width: 12 },
    rightColumnGridItem: { width: [0, 0, 4, 4, 4] },
    rightColumnGridItemHidden: {
        below: "md",
        css: css`
            width: 100%;
            direction: ltr;
        `,
    },
    container: {
        css: css`
            direction: rtl;
        `,
    },
};

const styles = viewStyles;

/**
 * XNG-Change
 * Use Props instead of WidgetProps
 * Change Structure
 */
const ProductDetailsView: React.FC<Props> = ({
    id,
    // XNG-Changes
    isAuthenticated,
    product,
}) => {
    // XNG-Change
    const shouldTakeFullWidth = isAuthenticated && product.manufacturerItem;
    const shouldHideProductDescriptionDivider = !product.content || !product.content.htmlContent;

    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.leftColumnGridItem} data-test-selector="productDetails_leftColumn">
                <StyledWrapper {...styles.showOnMobile}>
                    <XngZone contentId={id} zoneName="Content2" hint="Product Title" />
                </StyledWrapper>
                <XngZone contentId={id} zoneName="Content0" hint="Image" />
                <Hidden {...styles.hideOnTablet}>
                    <ProductDetailsButtons widgetId={id} />
                </Hidden>
            </StyledWrapper>
            <StyledWrapper {...styles.rightColumnGridItem} data-test-selector="productDetails_rightColumn">
                <StyledWrapper {...styles.hideOnMobile}>
                    <XngZone contentId={id} zoneName="Content2" hint="Product Title" />
                </StyledWrapper>
                <StyledWrapper {...styles.productNumbersWrapper} {...{ shouldTakeFullWidth }}>
                    {createWidgetElement("ProductDetails/ProductDetailsCustomerPartNumber", {
                        fields: {},
                    })}
                    {createWidgetElement("ProductDetails/ProductDetailsPartNumber", {
                        fields: {},
                    })}
                    {createWidgetElement("ProductDetails/ProductDetailsManufacturingPartNumber", {
                        fields: {},
                    })}
                </StyledWrapper>
                <XngZone contentId={id} zoneName="Content3" hint="Info zone" />
                <XngZone contentId={id} zoneName="Content4" hint="Price" />
                <StyledWrapper {...styles.inventoryWrapper}>
                    <XngZone contentId={id} zoneName="Content8" hint="Inventory" />
                </StyledWrapper>
                <ProductActions widgetId={id} />
                <Hidden {...styles.hideOnTablet}>
                    <RequiredProducts />
                    {!shouldHideProductDescriptionDivider && <StyledWrapper {...styles.divider} />}
                    <XngZone contentId={id} zoneName="Content5" hint="Description" />
                </Hidden>
                <Hidden {...styles.showOnTablet}>
                    <ProductDetailsButtons widgetId={id} />
                </Hidden>
            </StyledWrapper>
            <Hidden {...styles.showOnTablet} className="full-width">
                <RequiredProducts />
                {!shouldHideProductDescriptionDivider && <StyledWrapper {...styles.divider} />}
                <XngZone contentId={id} zoneName="Content5" hint="Description" />
            </Hidden>
            <StyledWrapper {...styles.specificationsContainer}>
                <XngZone contentId={id} zoneName="Content6" hint="Zone For Product Specifications" />
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(connect(mapStateToProps)(ProductDetailsView)),
    definition: {
        displayName: "View",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
